import type { IntegrationType } from "@v2/features/settings/shop/integrations/IntegrationForm";
import type { z } from "zod";

import * as React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Alert } from "@replo/design-system/blocks/alert/Alert";
import { LabeledControl } from "@replo/design-system/blocks/labeled-control/LabeledControl";
import PageHeader from "@replo/design-system/blocks/page-header/PageHeader";
import { Badge } from "@replo/design-system/components/badge/Badge";
import { Button } from "@replo/design-system/components/button/Button";
import { Input } from "@replo/design-system/components/input/Input";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from "@replo/design-system/components/menu/Menu";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { Tag } from "@replo/design-system/components/tag/Tag";
import { toast } from "@replo/design-system/components/toast/Toast";
import { skipToken } from "@tanstack/react-query";
import DeleteConfirmationModal from "@v2/components/DeleteConfirmationModal";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import { INTEGRATION_CONFIGS } from "@v2/features/settings/shop/integrations/config";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import {
  integrationRouter,
  integrationUtils,
} from "@v2/trpc/routes/integration";
import { routes } from "@v2/utils/router";
import {
  ArrowLeft,
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
  FileText,
  MoreHorizontal,
  X,
} from "lucide-react";
import { useForm } from "react-hook-form";
import {
  generatePath,
  Navigate,
  useNavigate,
  useParams,
} from "react-router-dom";

const VALID_INTEGRATION_TYPES = new Set<string>(
  Object.keys(INTEGRATION_CONFIGS),
);

const INTEGRATION_DOCS_SLUG_MAP: Record<IntegrationType, string> = {
  GA4: "google-analytics-4",
  GoogleTagManager: "google-tag-manager",
  Hotjar: "hotjar",
  Klaviyo: "klaviyo",
  Meta: "meta-pixel",
  MicrosoftClarity: "microsoft-clarity",
  Pinterest: "pinterest-tag",
  Reddit: "reddit-pixel",
  Snapchat: "snapchat-pixel",
  TikTok: "tiktok-pixel",
  Triplewhale: "triplewhale",
};

function getIntegrationDocsUrl(integrationType: IntegrationType): string {
  const docsSlug =
    INTEGRATION_DOCS_SLUG_MAP[integrationType] ?? integrationType.toLowerCase();
  return `https://docs.replo.app/integrations/${docsSlug}`;
}

export const IntegrationDetailPage: React.FC = () => {
  const { integrationType: rawType } = useParams<{
    integrationType: string;
  }>();
  const projectId = useCurrentProjectId();
  const navigate = useNavigate();

  if (!rawType || !VALID_INTEGRATION_TYPES.has(rawType)) {
    return (
      <Navigate
        to={generatePath(routes.shops.integrations.list, {
          projectId: projectId ?? "",
        })}
        replace
      />
    );
  }

  const integrationType = rawType as IntegrationType;

  return (
    <IntegrationDetailContent
      integrationType={integrationType}
      projectId={projectId}
      onBack={() => {
        navigate(
          generatePath(routes.shops.integrations.list, {
            projectId: projectId ?? "",
          }),
        );
      }}
    />
  );
};

// ---------------------------------------------------------------------------
// Helper: format a date nicely
// ---------------------------------------------------------------------------

function formatDate(date: Date): string {
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatTime(date: Date): string {
  return new Date(date).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

// ---------------------------------------------------------------------------
// Timeline step component
// ---------------------------------------------------------------------------

const TimelineStep: React.FC<{
  title: string;
  subtitle: string;
  isLast?: boolean;
}> = ({ title, subtitle, isLast = false }) => (
  <div className="flex gap-3">
    {/* Vertical line + dot */}
    <div className="flex flex-col items-center pt-1.5">
      <div className="size-2 shrink-0 rounded-full bg-border" />
      {!isLast && <div className="w-px flex-1 bg-border" />}
    </div>

    {/* Content */}
    <div className="flex flex-col gap-0.5 pb-5">
      <span className="text-sm font-medium text-default">{title}</span>
      <span className="text-xs text-muted">{subtitle}</span>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Connection block
// ---------------------------------------------------------------------------

type ConnectionBlockProps = {
  integrationType: IntegrationType;
  entityName: string;
  entityLogoUrl?: string | null;
  projectId: string | undefined;
  workspaceId: string | null;
  existingIntegration: {
    id: string;
    config: unknown;
    createdAt: Date;
    updatedAt: Date;
  } | null;
  isExpired?: boolean;
  onSave: () => void;
  onDelete: () => void;
};

const ConnectionBlock: React.FC<ConnectionBlockProps> = ({
  integrationType,
  entityName,
  entityLogoUrl,
  projectId,
  workspaceId,
  existingIntegration,
  isExpired = false,
  onSave,
  onDelete,
}) => {
  const config = INTEGRATION_CONFIGS[integrationType];
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [isExpanded, setIsExpanded] = React.useState(
    !existingIntegration || isExpired,
  );
  const logEvent = useLogAnalytics();
  const isConnected = existingIntegration !== null;

  const { mutate: createOrUpdate, isPending: isSaving } =
    integrationRouter.createOrUpdate.useMutation({
      onSuccess: () => {
        // Keep expanded after successful connection/update
        setIsExpanded(true);
        toast.success({
          title: isConnected ? "Integration Updated" : "Integration Connected",
          description: isConnected
            ? "Your integration has been updated."
            : "Your integration has been connected successfully.",
        });
        onSave();
      },
      onError: () => {
        toast.error({
          title: isConnected ? "Update Failed" : "Connection Failed",
          description:
            "Something went wrong. Please check your credentials and try again.",
        });
      },
    });

  const { mutate: deleteIntegration, isPending: isDeleting } =
    integrationRouter.deleteIntegrationFromProject.useMutation({
      onSuccess: () => {
        toast.success({
          title: "Integration Disconnected",
          description: "Your integration has been disconnected.",
        });
        onDelete();
      },
      onError: () => {
        toast.error({
          title: "Disconnect Failed",
          description: "Something went wrong while disconnecting.",
        });
      },
    });

  type FormData = z.infer<
    (typeof INTEGRATION_CONFIGS)[typeof integrationType]["schema"]
  >;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(config.schema),
    defaultValues: (existingIntegration?.config as FormData) ?? undefined,
  });

  const onSubmit = handleSubmit((data: FormData) => {
    if (!projectId || !workspaceId) {
      toast.error({
        title: isConnected ? "Update Failed" : "Connection Failed",
        description:
          "Missing project or workspace context. Please refresh and try again.",
      });
      return;
    }
    logEvent("integrations.add", {
      integration: integrationType,
    });
    createOrUpdate({
      type: integrationType,
      config: data,
      workspaceId,
      projectId,
      id: existingIntegration?.id,
    });
  });

  // Build timeline events for this integration
  const timelineEvents: {
    title: string;
    subtitle: string;
  }[] = [];

  if (existingIntegration) {
    const createdAt = new Date(existingIntegration.createdAt);
    const updatedAt = new Date(existingIntegration.updatedAt);
    const wasUpdated = updatedAt.getTime() - createdAt.getTime() > 60_000;

    if (wasUpdated) {
      timelineEvents.push({
        title: "Configuration updated",
        subtitle: `${formatDate(updatedAt)} at ${formatTime(updatedAt)}`,
      });
    }

    timelineEvents.push({
      title: "Integration connected",
      subtitle: `${formatDate(createdAt)} at ${formatTime(createdAt)}`,
    });
  }

  return (
    <div className="flex flex-col rounded-xl border-0.5 border-border shadow-xs">
      {/* Header */}
      <div className="flex flex-col px-5 pt-5 pb-5 border-b-0.5 border-border">
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="text-sm font-medium text-default">Connection</span>
          {isConnected && (
            <Menu>
              <MenuTrigger
                render={
                  <Button variant="tertiary" size="base" icon>
                    <MoreHorizontal size={14} />
                  </Button>
                }
              />
              <MenuContent>
                <MenuItem
                  label="Disconnect"
                  variant="destructive"
                  onClick={() => setShowDeleteModal(true)}
                />
              </MenuContent>
            </Menu>
          )}
        </div>
        <span className="text-xs text-muted">
          This integration applies to the current shop.
        </span>
      </div>

      {/* Expired notice — gentle nudge to refresh credentials */}
      {isConnected && isExpired && (
        <div className="mx-5 mt-4">
          <Alert
            variant="info"
            size="inline"
            title="This integration has expired"
            description="Re-enter your credentials below to reconnect. Until refreshed, data from this integration won't sync."
            UNSAFE_className="w-full"
          />
        </div>
      )}

      {/* Entity row — only show when connected */}
      {isConnected && (
        <div className="flex items-center gap-3 px-5 py-5">
          {/* Entity avatar */}
          <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-subtle">
            {entityLogoUrl ? (
              <img
                src={entityLogoUrl}
                alt={entityName}
                className="size-9 rounded-lg object-contain"
              />
            ) : (
              <div className="size-9 rounded-lg bg-placeholder/20" />
            )}
          </div>

          {/* Entity name + enablement info */}
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-sm font-medium text-default">
              {entityName}
            </span>
            {isConnected && existingIntegration && (
              <span className="text-xs text-muted">
                Enabled {formatDate(new Date(existingIntegration.createdAt))}
              </span>
            )}
          </div>

          {/* Status */}
          <div className="flex shrink-0 items-center gap-1.5">
            {isConnected ? (
              <Button
                variant="tertiary"
                size="base"
                onClick={() => setIsExpanded(!isExpanded)}
                startEnhancer={
                  <div
                    className={`size-1.5 rounded-full ${isExpired ? "bg-sky" : "bg-positive-default"}`}
                  />
                }
                endEnhancer={
                  isExpanded ? (
                    <ChevronUp size={14} className="text-muted" />
                  ) : (
                    <ChevronDown size={14} className="text-muted" />
                  )
                }
              >
                {isExpired ? "Action Required" : "Connected"}
              </Button>
            ) : (
              <Tag variant="neutral" size="sm">
                Not connected
              </Tag>
            )}
          </div>
        </div>
      )}

      {/* Configuration form — always show when not connected, collapsible when connected */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          !isConnected || isExpanded
            ? "max-h-[1000px] opacity-100"
            : "max-h-0 opacity-0"
        }`}
      >
        <div
          className={`flex flex-col gap-6 px-5 ${isConnected ? "pt-0" : "pt-5"} pb-5`}
        >
          <div className="flex flex-col gap-4">
            {Object.entries(config.fields).map(([fieldName, fieldConfig]) => {
              const errorMessage = errors[fieldName as keyof typeof errors];
              const helpUrl =
                "helpUrl" in fieldConfig ? fieldConfig.helpUrl : null;
              const helpText =
                "helpText" in fieldConfig ? fieldConfig.helpText : null;

              return (
                <LabeledControl
                  key={fieldName}
                  label={fieldConfig.label}
                  size="sm"
                  error={errorMessage?.message}
                  labelAction={
                    helpUrl && helpText ? (
                      <Button
                        variant="link"
                        size="sm"
                        endEnhancer={<ArrowUpRight size={12} />}
                        onClick={() => {
                          window.open(helpUrl, "_blank", "noopener,noreferrer");
                        }}
                      >
                        {helpText}
                      </Button>
                    ) : undefined
                  }
                >
                  <Input
                    {...register(fieldName as keyof FormData)}
                    placeholder={fieldConfig.placeholder}
                    size="base"
                  />
                </LabeledControl>
              );
            })}
          </div>
          <div className="flex justify-end">
            <Button
              variant="primary"
              size="base"
              loading={isSaving}
              onClick={() => void onSubmit()}
            >
              {isConnected ? "Save Changes" : "Connect"}
            </Button>
          </div>
        </div>
      </div>

      {/* Activity timeline — only for connected integrations, and only when expanded */}
      {isConnected && timelineEvents.length > 0 && (
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isExpanded
              ? "max-h-[500px] opacity-100 border-t border-dashed border-border"
              : "max-h-0 opacity-0"
          }`}
        >
          <div className="flex flex-col gap-3 px-5 py-5">
            <span className="text-sm font-medium text-default">Activity</span>
            <div className="flex flex-col">
              {timelineEvents.map((event, index) => (
                <TimelineStep
                  key={index}
                  title={event.title}
                  subtitle={event.subtitle}
                  isLast={index === timelineEvents.length - 1}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && existingIntegration && (
        <DeleteConfirmationModal
          assetName={config.title}
          assetTypeDisplayName="Integration"
          confirmationType="delete"
          extraMessage="This action cannot be undone. Any features relying on this integration will stop working."
          onDelete={() => {
            if (!projectId) {
              toast.error({
                title: "Disconnect Failed",
                description:
                  "Missing project context. Please refresh and try again.",
              });
              return;
            }
            deleteIntegration({
              projectId,
              integrationId: existingIntegration.id,
            });
            setShowDeleteModal(false);
          }}
          onRequestClose={() => setShowDeleteModal(false)}
          isLoading={isDeleting}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Inner content (separated so hooks are below the early return)
// ---------------------------------------------------------------------------

export const IntegrationDetailContent: React.FC<{
  integrationType: IntegrationType;
  projectId: string | undefined;
  onBack: () => void;
  inModal?: boolean;
}> = ({ integrationType, projectId, onBack, inModal = false }) => {
  const workspaceId = useCurrentWorkspaceId();
  const config = INTEGRATION_CONFIGS[integrationType];

  // Query integrations for this shop (project)
  const { data: shopIntegrations, isLoading: isLoadingShop } =
    integrationRouter.find.useQuery(projectId ? { projectId } : skipToken);

  const existingIntegration =
    (shopIntegrations ?? []).find((integration) => {
      return integration.type === integrationType;
    }) ?? null;

  if (isLoadingShop) {
    return (
      <div className="flex flex-col w-full h-full max-w-[800px] mx-auto gap-6">
        <Skeleton className="h-6 w-16" />
        <div className="flex items-center gap-3">
          <Skeleton className="size-10 rounded-lg" />
          <div className="flex flex-col gap-1.5">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-4 w-64" />
          </div>
        </div>
        <Skeleton className="h-[200px] w-full rounded-xl" />
      </div>
    );
  }

  const entityData = existingIntegration
    ? {
        entityName: config.title, // Placeholder: use integration title until we have org name
        entityLogoUrl: null, // TODO: Fetch organization logo from integration
      }
    : {
        entityName: "",
        entityLogoUrl: null,
      };

  const hasAnyConnection = existingIntegration !== null;

  // NOTE (Jasmine, 2026-02-17): Placeholder for expired check — will be driven
  // by backend data (e.g. token expiry, failed health check) in the future.
  const isAnyConnectionExpired = false;

  let connectionStatusTag: {
    variant: "info" | "success" | "neutral";
    label: string;
  };
  if (hasAnyConnection && isAnyConnectionExpired) {
    connectionStatusTag = { variant: "info", label: "Action Required" };
  } else if (hasAnyConnection) {
    connectionStatusTag = { variant: "success", label: "Connected" };
  } else {
    connectionStatusTag = { variant: "neutral", label: "Not connected" };
  }

  return (
    <div
      className={
        inModal
          ? "flex flex-col w-full"
          : "flex flex-col w-full h-full max-w-[800px] mx-auto"
      }
    >
      {/* Header */}
      <div className="relative w-full">
        {!inModal && (
          <div className="mb-4">
            <Button
              onClick={onBack}
              variant="tertiary"
              size="base"
              startEnhancer={<ArrowLeft />}
              UNSAFE_className="px-0 hover:bg-transparent hover:text-default/60 gap-1"
            >
              Integrations
            </Button>
          </div>
        )}
        <div className="relative w-full">
          {inModal && (
            <div className="absolute right-0 top-0 z-10">
              <Button
                variant="tertiary"
                size="base"
                icon
                onClick={onBack}
                aria-label="Close modal"
              >
                <X size={14} />
              </Button>
            </div>
          )}
          <PageHeader
            showDivider={false}
            title={
              <div className="flex items-center gap-3">
                <img
                  src={config.image}
                  alt={config.title}
                  className="size-10 rounded-lg object-contain"
                />
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span className="header-h3 text-default">
                      {config.title}
                    </span>
                    <Tag variant={connectionStatusTag.variant} size="base">
                      {connectionStatusTag.label}
                    </Tag>
                  </div>
                  <span className="text-sm text-muted font-normal leading-relaxed">
                    {config.description}
                  </span>
                </div>
              </div>
            }
          />
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-col gap-3 py-8">
        <ConnectionBlock
          integrationType={integrationType}
          projectId={projectId}
          workspaceId={workspaceId ?? null}
          isExpired={isAnyConnectionExpired && existingIntegration !== null}
          existingIntegration={
            existingIntegration
              ? {
                  id: existingIntegration.id,
                  config: existingIntegration.config,
                  createdAt: existingIntegration.createdAt,
                  updatedAt: existingIntegration.updatedAt,
                }
              : null
          }
          onSave={() => {
            // Refresh queries - stay in modal
            void integrationUtils.find.invalidate();
            void integrationUtils.get.invalidate();
          }}
          onDelete={() => {
            // Refresh queries - stay in modal
            void integrationUtils.find.invalidate();
            void integrationUtils.get.invalidate();
          }}
          {...entityData}
        />

        {/* Docs card */}
        <button
          type="button"
          onClick={() => {
            window.open(
              getIntegrationDocsUrl(integrationType),
              "_blank",
              "noopener,noreferrer",
            );
          }}
          className="flex items-center gap-3 rounded-lg border-0.5 border-border bg-white px-5 py-5 hover:bg-button-secondary-hover transition-colors shadow-xs"
        >
          <Badge size="xl" className="bg-subtle">
            <FileText className="text-muted" />
          </Badge>
          <span className="text-sm font-medium text-default">
            View documentation
          </span>
          <ArrowUpRight size={16} className="text-muted ml-auto" />
        </button>
      </div>
    </div>
  );
};
